import React from 'react';
import FeaturePage from './FeaturePage';
import { Box, Grid, Typography, Paper, List, ListItem, ListItemIcon, ListItemText, Divider, Container } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import InventoryIcon from '@mui/icons-material/Inventory';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import NotesIcon from '@mui/icons-material/Notes';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import SpeedIcon from '@mui/icons-material/Speed';
import GroupIcon from '@mui/icons-material/Group';

import inventoryTableScreenshot from '../assets/inventory-table-screenshot.png';
import inventoryMetricsScreenshot from '../assets/inventory-metrics-screenshot.png';
import { motion } from 'framer-motion';

// Animation Variants
const sectionVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0 }
};

const imageVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.7, ease: "easeOut" } }
};


function InventoryManagementPage() {

  const trackingFeatures = [
    { icon: <InventoryIcon color="primary"/>, text: "Comprehensive Vehicle Records: VIN, make, model, trim, mileage, color, purchase details." },
    { icon: <AttachMoneyIcon color="primary"/>, text: "Detailed Cost Tracking: Purchase price, reconditioning, and total investment per vehicle." },
    { icon: <SpeedIcon color="primary"/>, text: "Days in Inventory: Instantly see aging stock to optimize turnover." },
    { icon: <SyncAltIcon color="primary"/>, text: "Status Management: Update vehicle status (Available, Sold, Pending) with ease." },
    { icon: <NotesIcon color="primary"/>, text: "Issue Tracking: Log and manage notes or pending issues specific to each vehicle." },
  ];

  const profitFeatures = [
     { icon: <GroupIcon color="success"/>, text: "Purchaser Linking: Connect sold vehicles to purchaser details for complete deal history." },
     { icon: <ShowChartIcon color="success"/>, text: "Profitability Insights: View total costs, potential profit, and average profit per car directly." },
     { icon: <ShowChartIcon color="success"/>, text: "Performance Metrics: Analyze profit breakdown by purchaser and funding source." },
  ];


  return (
    <>
      <FeaturePage title="Unified Inventory & Profit Management">
        <Container maxWidth="xl" disableGutters sx={{ py: { xs: 4, md: 6 } }}>

          {/* Introductory Text */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
            style={{ marginBottom: '4rem', textAlign: 'center' }}
          >
            <Typography variant="h4" component="h1" sx={{ mb: 2, fontWeight: 600 }}>
              Master Your Stock, Maximize Your Profit.
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary', maxWidth: '850px', mx: 'auto' }}>
              DealCost integrates detailed vehicle tracking with powerful cost and profit analysis, giving you complete control and clarity over your dealership's performance.
            </Typography>
          </motion.div>

          {/* Section 1: Inventory Tracking */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={sectionVariants} sx={{ mb: { xs: 6, md: 8 } }}>
            <motion.div variants={imageVariants} sx={{ mb: 4 }}>
               <Paper elevation={6} sx={{ overflow: 'hidden', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.08)' }}>
                 <Box component="img" src={inventoryTableScreenshot} alt="DealCost Inventory Table" sx={{ width: '100%', display: 'block' }}/>
               </Paper>
               <Typography variant="caption" display="block" align="center" sx={{ mt: 1.5, color: 'text.secondary' }}>
                  Detailed inventory view with costs, status, and aging.
               </Typography>
            </motion.div>

            <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
              <Typography variant="h5" sx={{ fontWeight: 500, mb: 3, textAlign: 'center' }}>Core Tracking Capabilities</Typography>
              <List>
                {trackingFeatures.map((feature, index) => (
                  <motion.div key={index} variants={itemVariants} custom={index} initial="hidden" animate="visible" transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}>
                    <ListItem disablePadding sx={{ mb: 2, alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: '40px', mt: '4px' }}>
                        {feature.icon}
                      </ListItemIcon>
                      <ListItemText primary={feature.text} primaryTypographyProps={{ variant: 'body1' }} />
                    </ListItem>
                  </motion.div>
                ))}
              </List>
            </Box>
          </motion.div>

          <Divider sx={{ my: { xs: 4, md: 8 }, borderColor: 'rgba(0,0,0,0.08)' }} />

          {/* Section 2: Profit Analysis */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={sectionVariants} sx={{ mb: { xs: 4, md: 6 } }}>
             <motion.div variants={imageVariants} sx={{ mb: 4 }}>
               <Paper elevation={6} sx={{ overflow: 'hidden', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.08)' }}>
                 <Box component="img" src={inventoryMetricsScreenshot} alt="DealCost Profit Metrics" sx={{ width: '100%', display: 'block' }}/>
               </Paper>
               <Typography variant="caption" display="block" align="center" sx={{ mt: 1.5, color: 'text.secondary' }}>
                  Integrated profit analysis and performance metrics.
               </Typography>
             </motion.div>

             <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
               <Typography variant="h5" sx={{ fontWeight: 500, mb: 3, textAlign: 'center' }}>Unlock Financial Clarity</Typography>
               <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, textAlign: 'center' }}>
                  Stop guessing. Know your exact costs, analyze profitability per vehicle and purchaser, and make smarter decisions backed by real-time data.
                </Typography>
               <List>
                 {profitFeatures.map((feature, index) => (
                   <motion.div key={index} variants={itemVariants} custom={index} initial="hidden" animate="visible" transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}>
                     <ListItem disablePadding sx={{ mb: 2, alignItems: 'flex-start' }}>
                       <ListItemIcon sx={{ minWidth: '40px', mt: '4px' }}>
                         {feature.icon}
                       </ListItemIcon>
                       <ListItemText primary={feature.text} primaryTypographyProps={{ variant: 'body1' }} />
                     </ListItem>
                   </motion.div>
                 ))}
               </List>
             </Box>
          </motion.div>

        </Container>
      </FeaturePage>
    </>
  );
}

export default InventoryManagementPage;